import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Skeleton,
    Stack,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { DistributionCenter } from '@api/DistributionCenters/DistributionCenter';
import { UserType } from '@api/Users/User';
import UserService from '@api/Users/UserCtrl';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setDistributionCenterSelected } from '@store/authStore';

interface ModalViewCedisProps {
    modalOpen: boolean;
    onClose: () => void;
    distributionCenter: DistributionCenter | undefined;
}

export const ModalViewCedis: React.FunctionComponent<ModalViewCedisProps> = ({
    modalOpen,
    onClose,
    distributionCenter
}) => {
    const navigation = useNavigate();
    const dispatch = useDispatch();
    const userService = useMemo(() => new UserService(), []);
    const [manager, setManager] = useState<UserType | undefined>(undefined);

    const getData = async (managerId?: string) => {
        if (!managerId) return;
        showLoader();
        try {
            const managerData = await userService.getUser(managerId);
            setManager(managerData);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        if (modalOpen && distributionCenter?.managerId) {
            void getData(distributionCenter.managerId);
        } else {
            setManager(undefined);
        }
    }, [modalOpen, distributionCenter]);

    const onViewInventory = () => {
        onClose();
        if (distributionCenter?.id) {
            dispatch(setDistributionCenterSelected(distributionCenter));
            navigation('inventory');
        }
    };

    const onViewOrders = () => {
        onClose();
        if (distributionCenter?.id) {
            dispatch(setDistributionCenterSelected(distributionCenter));
            navigation('orders');
        }
    };

    if (!distributionCenter) {
        return null;
    }

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalOpen}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Detalles del CEDIS
                    </Typography>
                    <IconButton color="secondary" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3}>
                    <Stack>
                        <Typography variant="h5" fontWeight="600">
                            {distributionCenter.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {distributionCenter.phone}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {distributionCenter.email}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {distributionCenter.address}
                        </Typography>
                    </Stack>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                            Información del Encargado
                        </Typography>
                        {manager ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nombre
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {manager.profile.name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Teléfono
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {manager.profile.phone}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Correo
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {manager.email}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ) : (
                            <Skeleton variant="rectangular" height={60} />
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 4 }}>
                <Button variant="outlined" color="primary" startIcon={<InventoryIcon />} onClick={onViewInventory}>
                    Ver inventario
                </Button>
                <Button variant="outlined" color="primary" startIcon={<ShoppingCartIcon />} onClick={onViewOrders}>
                    Ver pedidos
                </Button>
            </DialogActions>
        </Dialog>
    );
};
